import { useState, useEffect } from 'react'
import * as Sentry from '@sentry/react'
import { Button, Table, Tag, Spin, message, Empty, Collapse } from 'antd'
import {
  CreditCardOutlined,
  FileTextOutlined,
  SettingOutlined,
  WarningOutlined,
  CheckOutlined,
  CloseOutlined,
  DownOutlined,
} from '@ant-design/icons'
import styled from 'styled-components'
import {
  PaymentService,
  type SubscriptionInfo,
  type Invoice,
} from '../../services/payments/paymentService'
import { ProfileService, type TierComparisonData } from '../../services/profile/profileService'
import { useFeatureAccessContext } from '../../context/featureAccess'
import { SPACING, COLORS, BORDER_RADIUS, TYPOGRAPHY, FONT_WEIGHT } from '../../styles/theme'

const Section = styled.div`
  margin-bottom: ${SPACING.xl};
`

const SectionTitle = styled.h3`
  font-size: ${TYPOGRAPHY.body.fontSize};
  font-weight: ${FONT_WEIGHT.semibold};
  margin-bottom: ${SPACING.md};
  display: flex;
  align-items: center;
  gap: ${SPACING.sm};
  color: var(--color-text-primary);
`

const SubscriptionCard = styled.div`
  background: var(--color-bg-secondary);
  border: 1px solid var(--color-border);
  border-radius: ${BORDER_RADIUS.lg};
  padding: ${SPACING.lg};
  margin-bottom: ${SPACING.lg};
`

const SubscriptionStatus = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: ${SPACING.md};
`

const PlanInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING.sm};
`

const PlanName = styled.span`
  font-size: ${TYPOGRAPHY.body.fontSize};
  font-weight: ${FONT_WEIGHT.semibold};
  color: var(--color-text-primary);
`

const FeaturesCollapse = styled(Collapse)`
  background: transparent;
  border: none;
  margin-top: ${SPACING.md};

  .ant-collapse-item {
    border: none !important;
  }

  .ant-collapse-header {
    padding: ${SPACING.sm} 0 !important;
    color: var(--color-text-secondary) !important;
    font-size: ${TYPOGRAPHY.caption.fontSize};

    &:hover {
      color: ${COLORS.primary} !important;
    }
  }

  .ant-collapse-content {
    border: none;
    background: transparent;
    overflow: hidden;
  }

  .ant-collapse-content-hidden {
    display: none;
  }

  .ant-collapse-content-box {
    padding: ${SPACING.sm} 0 !important;
  }
`

const FeaturesList = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${SPACING.xs} ${SPACING.lg};

  @media (max-width: 500px) {
    grid-template-columns: 1fr;
  }
`

const FeatureItem = styled.div<{ $included: boolean }>`
  display: flex;
  align-items: center;
  gap: ${SPACING.xs};
  font-size: ${TYPOGRAPHY.caption.fontSize};
  color: ${({ $included }) =>
    $included ? 'var(--color-text-secondary)' : 'var(--color-text-tertiary)'};

  .anticon {
    font-size: ${TYPOGRAPHY.caption.fontSize};
    color: ${({ $included }) => ($included ? '#16a34a' : 'var(--color-text-tertiary)')};
  }
`

const StatusBadge = styled.span<{ $status: 'active' | 'trialing' | 'canceling' | 'past_due' }>`
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  border-radius: ${BORDER_RADIUS.full};
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.medium};
  ${({ $status }) => {
    switch ($status) {
      case 'active':
        return `
          background: rgba(34, 197, 94, 0.1);
          color: #16a34a;
        `
      case 'trialing':
        return `
          background: rgba(59, 130, 246, 0.1);
          color: #2563eb;
        `
      case 'canceling':
        return `
          background: rgba(245, 158, 11, 0.1);
          color: #d97706;
        `
      case 'past_due':
        return `
          background: rgba(239, 68, 68, 0.1);
          color: #dc2626;
        `
    }
    return undefined
  }}
`

const PeriodInfo = styled.p<{ $warning?: boolean }>`
  color: ${(props) => (props.$warning ? '#d97706' : 'var(--color-text-secondary)')};
  font-size: ${TYPOGRAPHY.caption.fontSize};
  margin: ${SPACING.sm} 0 0 0;
`

const CancellationNotice = styled.div`
  background: linear-gradient(135deg, rgba(217, 119, 6, 0.1) 0%, rgba(234, 179, 8, 0.1) 100%);
  border: 1px solid rgba(217, 119, 6, 0.3);
  border-radius: ${BORDER_RADIUS.md};
  padding: ${SPACING.md};
  margin-top: ${SPACING.md};
`

const CancellationText = styled.p`
  color: #d97706;
  font-size: ${TYPOGRAPHY.caption.fontSize};
  margin: 0;
  font-weight: ${FONT_WEIGHT.medium};
`

const CancellationSubtext = styled.p`
  color: var(--color-text-secondary);
  font-size: ${TYPOGRAPHY.caption.fontSize};
  margin: ${SPACING.xs} 0 0 0;
`

const PastDueNotice = styled.div`
  background: linear-gradient(135deg, rgba(220, 38, 38, 0.1) 0%, rgba(239, 68, 68, 0.1) 100%);
  border: 1px solid rgba(220, 38, 38, 0.3);
  border-radius: ${BORDER_RADIUS.md};
  padding: ${SPACING.md};
  margin-top: ${SPACING.md};
`

const PastDueText = styled.p`
  color: #dc2626;
  font-size: ${TYPOGRAPHY.caption.fontSize};
  margin: 0;
  font-weight: ${FONT_WEIGHT.medium};
`

const PastDueSubtext = styled.p`
  color: var(--color-text-secondary);
  font-size: ${TYPOGRAPHY.caption.fontSize};
  margin: ${SPACING.xs} 0 0 0;
`

const UpgradeButton = styled(Button)`
  background: linear-gradient(135deg, ${COLORS.primary} 0%, #9b7dfc 100%);
  border: none;

  &:hover {
    background: linear-gradient(135deg, #5a43d9 0%, #8a6aec 100%);
  }
`

const LoadingContainer = styled.div`
  text-align: center;
  padding: ${SPACING.xxl};
`

export function BillingSection() {
  const { featureAccess } = useFeatureAccessContext()
  const [subscription, setSubscription] = useState<SubscriptionInfo | null>(null)
  const [invoices, setInvoices] = useState<Invoice[]>([])
  const [tierData, setTierData] = useState<TierComparisonData | null>(null)
  const [loading, setLoading] = useState(true)
  const [portalLoading, setPortalLoading] = useState(false)

  useEffect(() => {
    void loadBillingData()

    // Check if returning from Stripe portal - refetch with delay for webhook processing
    const returningFromPortal = sessionStorage.getItem('stripe_portal_redirect')
    if (returningFromPortal) {
      sessionStorage.removeItem('stripe_portal_redirect')
      // Give webhook time to process, then refetch
      setTimeout(() => {
        void (async () => {
          const subResponse = await PaymentService.getSubscription()
          if (subResponse.data) {
            setSubscription(subResponse.data.subscription)
          }
        })()
      }, 1500)
    }
  }, [])

  const loadBillingData = async () => {
    setLoading(true)
    try {
      const [subResponse, invResponse, tierResponse] = await Promise.all([
        PaymentService.getSubscription(),
        PaymentService.getInvoices(),
        ProfileService.getTierComparison(),
      ])

      if (subResponse.data) {
        setSubscription(subResponse.data.subscription)
      }
      if (invResponse.data) {
        setInvoices(invResponse.data.invoices)
      }
      if (tierResponse.data) {
        setTierData(tierResponse.data)
      }
    } catch (err) {
      console.error('Failed to load billing data:', err)
      Sentry.captureException(err, { tags: { feature: 'settings', action: 'load_billing_data' } })
    } finally {
      setLoading(false)
    }
  }

  const handleManageSubscription = async () => {
    setPortalLoading(true)
    try {
      const response = await PaymentService.createPortalSession()
      if (response.data?.url) {
        // Mark that we're going to portal so we can refetch on return
        sessionStorage.setItem('stripe_portal_redirect', '1')
        window.location.href = response.data.url
      } else if (response.error) {
        message.error(response.error.message || 'Failed to open billing portal')
      }
    } catch (error) {
      Sentry.captureException(error, {
        tags: { feature: 'settings', action: 'open_billing_portal' },
      })
      message.error('Failed to open billing portal')
    } finally {
      setPortalLoading(false)
    }
  }

  const formatDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  const formatAmount = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount / 100)
  }

  const invoiceColumns = [
    {
      title: 'Date',
      dataIndex: 'created',
      key: 'date',
      render: (created: number) => formatDate(created),
    },
    {
      title: 'Amount',
      key: 'amount',
      render: (record: Invoice) => formatAmount(record.amount, record.currency),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={status === 'paid' ? 'green' : status === 'open' ? 'orange' : 'default'}>
          {status}
        </Tag>
      ),
    },
    {
      title: 'Invoice',
      key: 'actions',
      render: (record: Invoice) => (
        <Button
          type="link"
          size="small"
          icon={<FileTextOutlined />}
          href={record.invoiceUrl ?? record.pdfUrl ?? '#'}
          target="_blank"
          disabled={!record.invoiceUrl && !record.pdfUrl}
        >
          View
        </Button>
      ),
    },
  ]

  if (loading) {
    return (
      <LoadingContainer>
        <Spin />
      </LoadingContainer>
    )
  }

  const isPaid = featureAccess?.tier === 'paid'
  const isActive = subscription?.status === 'active' || subscription?.status === 'trialing'

  return (
    <div>
      <Section>
        <SectionTitle>
          <CreditCardOutlined />
          Subscription
        </SectionTitle>

        <SubscriptionCard>
          <SubscriptionStatus>
            <PlanInfo>
              <PlanName>{isPaid ? 'Pro' : 'Free'}</PlanName>
              {isPaid && subscription?.status ? (
                <StatusBadge
                  $status={
                    subscription.status === 'past_due'
                      ? 'past_due'
                      : subscription.cancelAtPeriodEnd
                        ? 'canceling'
                        : subscription.status === 'trialing'
                          ? 'trialing'
                          : 'active'
                  }
                >
                  {subscription.status === 'past_due'
                    ? 'Payment Failed'
                    : subscription.cancelAtPeriodEnd
                      ? 'Canceling'
                      : subscription.status === 'trialing'
                        ? 'Trial'
                        : 'Active'}
                </StatusBadge>
              ) : !isPaid ? (
                <StatusBadge $status="active">Current Plan</StatusBadge>
              ) : null}
            </PlanInfo>

            {isPaid && (isActive || subscription?.status === 'past_due') ? (
              <Button
                icon={<SettingOutlined />}
                loading={portalLoading}
                onClick={handleManageSubscription}
                danger={subscription.status === 'past_due'}
              >
                {subscription.status === 'past_due' ? 'Update Payment' : 'Manage Subscription'}
              </Button>
            ) : (
              <UpgradeButton type="primary" href="/upgrade">
                Upgrade to Pro
              </UpgradeButton>
            )}
          </SubscriptionStatus>

          {subscription?.currentPeriodEnd && isPaid && !subscription.cancelAtPeriodEnd && (
            <PeriodInfo>
              {subscription.status === 'trialing'
                ? `Trial ends: ${new Date(subscription.currentPeriodEnd).toLocaleDateString()}`
                : `Next billing date: ${new Date(subscription.currentPeriodEnd).toLocaleDateString()}`}
            </PeriodInfo>
          )}

          {subscription?.cancelAtPeriodEnd && isPaid && (
            <CancellationNotice>
              <CancellationText>
                Your subscription will end on{' '}
                {subscription.currentPeriodEnd
                  ? new Date(subscription.currentPeriodEnd).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })
                  : 'the end of your billing period'}
              </CancellationText>
              <CancellationSubtext>
                You'll still have access to Pro features until then. You can resubscribe anytime
                from the Manage Subscription portal.
              </CancellationSubtext>
            </CancellationNotice>
          )}

          {subscription?.status === 'past_due' && (
            <PastDueNotice>
              <PastDueText>
                <WarningOutlined style={{ marginRight: 8 }} />
                Payment failed - please update your payment method
              </PastDueText>
              <PastDueSubtext>
                Your last payment was unsuccessful. Update your payment method to avoid losing
                access to Pro features. Click "Manage Subscription" above to update your card.
              </PastDueSubtext>
            </PastDueNotice>
          )}

          {tierData && (
            <FeaturesCollapse
              ghost
              expandIcon={({ isActive }) => (
                <DownOutlined
                  rotate={isActive ? 180 : 0}
                  style={{ fontSize: TYPOGRAPHY.caption.fontSize }}
                />
              )}
              items={[
                {
                  key: '1',
                  label: "What's included",
                  children: (
                    <FeaturesList>
                      {(() => {
                        const tier = isPaid ? tierData.paid : tierData.free
                        return Object.entries(tier.labels ?? {}).map(([key, label]) => {
                          const value = tier[key as keyof typeof tier] as
                            | boolean
                            | number
                            | undefined
                          const isIncluded = typeof value === 'boolean' ? value : true
                          const display =
                            typeof value === 'number'
                              ? value === 0
                                ? 'Unlimited'
                                : value.toLocaleString()
                              : null
                          return (
                            <FeatureItem key={key} $included={isIncluded}>
                              {isIncluded ? <CheckOutlined /> : <CloseOutlined />}
                              {display
                                ? `${display} ${(label ?? key).toLowerCase()}`
                                : (label ?? key)}
                            </FeatureItem>
                          )
                        })
                      })()}
                    </FeaturesList>
                  ),
                },
              ]}
            />
          )}
        </SubscriptionCard>
      </Section>

      <Section>
        <SectionTitle>
          <FileTextOutlined />
          Invoice History
        </SectionTitle>

        {invoices.length > 0 ? (
          <Table
            dataSource={invoices}
            columns={invoiceColumns}
            rowKey="id"
            pagination={{ pageSize: 5 }}
            size="small"
          />
        ) : (
          <Empty description="No invoices yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        )}
      </Section>
    </div>
  )
}
